import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withTranslation, WithTranslation } from 'react-i18next';
import './GoalCommentPage.css';

// Import SVG icons
// import { ReactComponent as ArrowLeftIcon } from '../../../assets/icons/arrow-left.svg';
import { ReactComponent as CameraIcon } from '../../../assets/icons/camera.svg';

// Import components
import GalleryComponent from '../../../components/GalleryComponent';
import GalleryDeleteComponent from '../../../components/GalleryDeleteComponent';
import ConfirmBox from '../../../components/ConfirmBox';

// Import Redux actions
import UpdateNotesOfGoalActions from '../../../redux/UpdateNotesOfGoalRedux';
import UploadImageToGoalActions from '../../../redux/UploadImageToGoalRedux';
import DeleteImageFromGoalActions from '../../../redux/DeleteImageFromGoalRedux';
import GetChildrenActions from '../../../redux/GetChildrenRedux';
import CurrentSettingsActions from '../../../redux/CurrentSettingsRedux';

interface GoalCommentPageProps extends WithTranslation {
  currentSettings?: any;
  navigate?: (path: string, options?: any) => void;
  location?: any;
  goalId?: string | number;
  updateNotesOfGoalFetching?: boolean;
  updateNotesOfGoalSuccess?: boolean;
  updateNotesOfGoalError?: boolean;
  uploadImageToGoalFetching?: boolean;
  uploadImageToGoalSuccess?: boolean;
  uploadImageToGoalError?: boolean;
  deleteImageFromGoalFetching?: boolean;
  deleteImageFromGoalSuccess?: boolean;
  deleteImageFromGoalError?: boolean;
  updateNotesOfGoal?: (id: number, notes: string) => void;
  uploadImageToGoal?: (id: number, image: string) => void;
  deleteImageFromGoal?: (id: number, imageId: string) => void;
  getChildren?: () => void;
  setCurrentChild?: (child: any) => void;
}

interface GoalCommentPageState {
  loading: boolean;
  hasCameraPermission: boolean | null;
  hasCameraRollPermission: boolean | null;
  type: string;
  isChanged: boolean;
  isDeleting: boolean;
  id: number;
  notes: string;
  albums: any[];
  props: any;
  showUploadModal: boolean;
  uploadProgress: number;
  selectedFile: File | null;
  permissionRequested: boolean;
  showActionSheet: boolean;
  actionSheetType: 'upload' | 'delete' | null;
  showAlert: boolean;
  alertType: 'success' | 'error' | 'warning' | null;
  alertTitle: string;
  alertMessage: string;
  showUnsavedWarning: boolean;
  pendingNavigation: string | null;
  deleteItem: any;
  deleteConfirm: boolean;
}

class GoalCommentPage extends Component<GoalCommentPageProps, GoalCommentPageState> {
  photoLimit = 4;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  
  constructor(props: GoalCommentPageProps) {
    super(props);
    
    // Initialize state with default values
    this.state = {
      loading: false,
      hasCameraPermission: null,
      hasCameraRollPermission: null,
      type: 'back',
      isChanged: false,
      isDeleting: false,
      id: 0,
      notes: '',
      albums: [],
      props: null,
      showUploadModal: false,
      uploadProgress: 0,
      selectedFile: null,
      permissionRequested: false,
      showActionSheet: false,
      actionSheetType: null,
      showAlert: false,
      alertType: null,
      alertTitle: '',
      alertMessage: '',
      showUnsavedWarning: false,
      pendingNavigation: null,
      deleteItem: null,
      deleteConfirm: false,
    };

    this.fileInputRef = React.createRef();
  }

  componentDidMount() {
    console.log('GoalCommentPage mounted');
    console.log('Goal ID:', this.props.goalId);
    console.log('Location state:', this.props.location?.state);
    
    // Handle back button for web
    window.addEventListener('beforeunload', this.handleBeforeUnload);

    if (this.props.getChildren) {
      this.props.getChildren();
    }
    
    // Load initial data if available
    this.loadInitialData();
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  }

  handleBeforeUnload = (event: BeforeUnloadEvent) => {
    // Show warning if there are unsaved changes
    if (this.state.isChanged) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  loadInitialData = () => {
    const { goalId, currentSettings } = this.props;
    
    if (goalId && currentSettings?.child?.goals) {
      const goal = currentSettings.child.goals.find((g: any) => g.id == goalId);
      if (goal) {
        this.setState({
          id: goal.id,
          notes: goal.parent_notes || '',
          albums: [...(goal.images || [])],
          loading: false,
        });
      } else {
        console.error('Goal not found for ID:', goalId);
        this.setState({ loading: false });
      }
    } else {
      console.log('Data not available yet, setting loading state');
      this.setState({ loading: true });
    }
  }

  _onBackPress = () => {
    const { navigate } = this.props;
    const { isChanged } = this.state;
    
    if (isChanged) {
      // Show unsaved changes warning
      this.setState({
        showUnsavedWarning: true,
        pendingNavigation: `/goals/individual/${this.state.id}`
      });
    } else if (navigate) {
      navigate(`/goals/individual/${this.state.id}`);
    }
  }

  _handleUnsavedChanges = (confirm: boolean) => {
    const { navigate } = this.props;
    const { pendingNavigation } = this.state;
    
    this.setState({
      showUnsavedWarning: false,
      pendingNavigation: null
    });
    
    if (confirm && navigate && pendingNavigation) {
      navigate(pendingNavigation);
    }
  }

  _requestCameraPermission = async () => {
    try {
      // For web, we'll simulate camera permission
      // In a real PWA, you might use MediaDevices API
      const permission = await navigator.mediaDevices?.getUserMedia({ video: true });
      if (permission) {
        this.setState({ hasCameraPermission: true });
        return true;
      }
    } catch (error) {
      console.log('Camera permission denied or not available');
    }
    
    this.setState({ hasCameraPermission: false });
    return false;
  }

  _requestGalleryPermission = async () => {
    // For web, file input doesn't require special permissions
    // This is more for consistency with mobile app structure
    this.setState({ hasCameraRollPermission: true });
    return true;
  }

  _onUploadPhoto = () => {
    // Check photo limit BEFORE showing action sheet (matches reference behavior)
    if (this.state.albums.length >= this.photoLimit) {
      this._showAlert(
        'error',
        this.props.t ? this.props.t('goal-comment-screen:alert.upload-photo.excess-limit.title') : 'Photo Limit Reached',
        this.props.t ? this.props.t('goal-comment-screen:alert.upload-photo.excess-limit.message') : `Maximum ${this.photoLimit} photos allowed. Please delete some photos first.`
      );
      return; // STOP here, don't show action sheet (matches reference)
    }

    // Show action sheet for upload source selection
    this.setState({ 
      showActionSheet: true, 
      actionSheetType: 'upload' 
    });
  }

  _handleActionSheetSelection = async (selection: 'camera' | 'gallery' | 'cancel') => {
    this.setState({ 
      showActionSheet: false, 
      actionSheetType: null 
    });

    if (selection === 'cancel') return;

    // Request permissions based on selection
    if (selection === 'camera') {
      const hasPermission = await this._requestCameraPermission();
      if (hasPermission) {
        this._openCamera();
      } else {
        this._showAlert(
          'error',
          this.props.t ? this.props.t('alert:allow-permission.camera.title') : 'Camera Permission Required',
          this.props.t ? this.props.t('alert:allow-permission.camera.message') : 'Please allow camera access to take photos.'
        );
      }
    } else if (selection === 'gallery') {
      const hasPermission = await this._requestGalleryPermission();
      if (hasPermission) {
        this._openGallery();
      }
    }
  }

  _openCamera = () => {
    // For web, we'll use a file input with camera capture
    if (this.fileInputRef.current) {
      this.fileInputRef.current.setAttribute('capture', 'environment');
      this.fileInputRef.current.click();
      // Reset capture attribute
      this.fileInputRef.current.removeAttribute('capture');
    }
  }

  _openGallery = () => {
    if (this.fileInputRef.current) {
      this.fileInputRef.current.click();
    }
  }

  _onFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        this._showAlert(
          'error',
          'Invalid File Type',
          'Please select an image file.'
        );
        return;
      }

      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        this._showAlert(
          'error',
          'File Too Large',
          'File size must be less than 5MB.'
        );
        return;
      }

      this.setState({ selectedFile: file }, () => {
        this.uploadSelectedFile();
      });
    }
  }

  uploadSelectedFile = () => {
    const { selectedFile, id } = this.state;
    const { uploadImageToGoal } = this.props;

    if (!selectedFile || !uploadImageToGoal || !id) {
      return;
    }

    // Convert file to base64
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64String = e.target?.result as string;
      if (base64String) {
        // Add to local state immediately for better UX
        const newPhoto = { url: base64String, id: Date.now().toString() };
        this.setState(prevState => ({
          albums: [...prevState.albums, newPhoto],
          isChanged: true,
          selectedFile: null,
        }));

        // Upload to server
        uploadImageToGoal(id, base64String);
      }
    };
    reader.readAsDataURL(selectedFile);
  }

  _onDeleteButtonPhoto = () => {
    this.setState(prevState => ({ 
      isDeleting: !prevState.isDeleting 
    }));
  }

  _onDeletePhoto = (key: number, imageId: string) => {
    const { deleteImageFromGoal } = this.props;
    const { albums, id } = this.state;

    if (!deleteImageFromGoal || !id) {
      return;
    }

    this.setState({
      deleteItem: {
        key,
        imageId,
      },
      deleteConfirm: true,
    });

    /*
    // Remove from local state immediately for better UX
    const updatedAlbums = [...albums];
    updatedAlbums.splice(key, 1);
    
    this.setState({
      albums: updatedAlbums,
      isChanged: true,
    });

    // Delete from server
    deleteImageFromGoal(id, imageId);
    */
  }

  _onDeleteConfirm = () => {
    this.setState({
      deleteConfirm: false,
    });

    const { deleteImageFromGoal } = this.props;
    const { albums, id } = this.state;

    if (this.state.deleteItem && deleteImageFromGoal) {

      // Remove from local state immediately for better UX
      const updatedAlbums = [...albums];
      updatedAlbums.splice(this.state.deleteItem.key, 1);
      
      this.setState({
        albums: updatedAlbums,
        isChanged: true,
      });

      deleteImageFromGoal(this.state.id, this.state.deleteItem.imageId);
    }
  }

  _onDeleteCancel = () => {
    this.setState({
      deleteConfirm: false,
      deleteItem: null,
    });
  }

  _onNotesChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const notes = event.target.value;
    this.setState({
      isChanged: true,
      notes: notes,
    });
  }

  _showAlert = (type: 'success' | 'error' | 'warning', title: string, message: string) => {
    this.setState({
      showAlert: true,
      alertType: type,
      alertTitle: title,
      alertMessage: message,
    });

    // Auto-hide success alerts after 3 seconds
    if (type === 'success') {
      setTimeout(() => {
        this.setState({ showAlert: false });
      }, 3000);
    }
  }

  _hideAlert = () => {
    this.setState({ showAlert: false });
  }

  _onSavePress = () => {
    const { updateNotesOfGoal, navigate } = this.props;
    const { id, notes, isChanged } = this.state;
    
    console.log('_onSavePress called:', { id, notes, isChanged, updateNotesOfGoal: !!updateNotesOfGoal });
    
    // Stop deleting mode first (matches reference behavior)
    if (this.state.isDeleting) {
      this.setState({ isDeleting: false });
    }
    
    if (updateNotesOfGoal && id ) {
      if (isChanged) {
        // Save if there are changes
        console.log('Calling updateNotesOfGoal with:', { id, notes });
        updateNotesOfGoal(id, notes);
        // Keep isChanged true until Redux responds - this ensures button stays in "Saving..." state
      } else {
        // Go back if no changes (matches reference behavior)
        console.log('No changes, navigating back');
        if (navigate) {
          navigate(`/goals/individual/${id}`);
        }
      }
    } else {
      console.log('Missing required data:', { updateNotesOfGoal: !!updateNotesOfGoal, id, notes });
    }
  }

  static getDerivedStateFromProps(nextProps: GoalCommentPageProps, prevState: GoalCommentPageState) {
    // Check if we have previous props to compare
    if (prevState.props) {
      // Check if notes update was successful
      if (!nextProps.updateNotesOfGoalFetching && nextProps.updateNotesOfGoalSuccess !== prevState.props.updateNotesOfGoalSuccess) {
        if (nextProps.updateNotesOfGoalSuccess) {
          console.log('Notes update successful, navigating back in 2 seconds');
          // Show success message and refresh data
          if (nextProps.getChildren) {
            nextProps.getChildren();
          }
          
          // Show success alert before auto-navigation (matches reference behavior)
          if (nextProps.navigate && typeof nextProps.navigate === 'function') {
            // Auto-navigate back after successful save (matches reference behavior)
            setTimeout(() => {
              if (nextProps.navigate) {
                nextProps.navigate(`/goals/individual/${prevState.id}`);
              }
            }, 2000); // 2 second delay to show success
          }
          
          return {
            isChanged: false,
            props: nextProps,
          };
        }
      }

      // Check if notes update failed
      if (!nextProps.updateNotesOfGoalFetching && nextProps.updateNotesOfGoalError !== prevState.props.updateNotesOfGoalError) {
        if (nextProps.updateNotesOfGoalError) {
          console.log('Notes update failed, keeping isChanged true for retry');
          // Keep isChanged true on error so user can retry
          return {
            props: nextProps,
          };
        }
      }

      // Check if image upload was successful
      if (!nextProps.uploadImageToGoalFetching && nextProps.uploadImageToGoalSuccess !== prevState.props.uploadImageToGoalSuccess) {
        if (nextProps.uploadImageToGoalSuccess) {
          console.log('Image upload successful');
          // Refresh data
          if (nextProps.getChildren) {
            nextProps.getChildren();
          }
          return {
            isChanged: false,
            props: nextProps,
          };
        }
      }

      // Check if image upload failed
      if (!nextProps.uploadImageToGoalFetching && nextProps.uploadImageToGoalError !== prevState.props.uploadImageToGoalError) {
        if (nextProps.uploadImageToGoalError) {
          console.log('Image upload failed, removing from local state');
          // Remove the failed upload from local state
          return {
            albums: prevState.albums.slice(0, -1),
            isChanged: false,
            props: nextProps,
          };
        }
      }

      // Check if image deletion was successful
      if (!nextProps.deleteImageFromGoalFetching && nextProps.deleteImageFromGoalSuccess !== prevState.props.deleteImageFromGoalSuccess) {
        if (nextProps.deleteImageFromGoalSuccess) {
          console.log('Image deletion successful');
          // Refresh data
          if (nextProps.getChildren) {
            nextProps.getChildren();
          }
          return {
            isChanged: false,
            props: nextProps,
          };
        }
      }

      // Check if image deletion failed
      if (!nextProps.deleteImageFromGoalFetching && nextProps.deleteImageFromGoalError !== prevState.props.deleteImageFromGoalError) {
        if (nextProps.deleteImageFromGoalError) {
          console.log('Image deletion failed, restoring to local state');
          // Restore the deleted image to local state
          return {
            albums: [...nextProps.currentSettings?.child?.goals?.find((g: any) => g.id === prevState.id)?.images || []],
            isChanged: false,
            props: nextProps,
          };
        }
      }
    }

    // Return null to indicate no change to state, or update props
    return {
      props: nextProps
    };
  }

  // Add componentDidUpdate to handle specific error alerts (matches reference behavior)
  componentDidUpdate(prevProps: GoalCommentPageProps) {
    // Show specific error alerts for failed operations
    if (prevProps.updateNotesOfGoalError !== this.props.updateNotesOfGoalError && this.props.updateNotesOfGoalError) {
      console.log('Showing save error alert');
      this._showAlert(
        'error',
        this.props.t ? this.props.t('goal-comment-screen:alert.save-fail.title') : 'Save Failed',
        this.props.t ? this.props.t('goal-comment-screen:alert.save-fail.message') : 'Failed to save notes. Please try again.'
      );
    }
    
    if (prevProps.uploadImageToGoalError !== this.props.uploadImageToGoalError && this.props.uploadImageToGoalError) {
      console.log('Showing upload error alert');
      this._showAlert(
        'error',
        this.props.t ? this.props.t('goal-comment-screen:alert.upload-fail.title') : 'Upload Failed',
        this.props.t ? this.props.t('goal-comment-screen:alert.upload-fail.message') : 'Failed to upload image. Please try again.'
      );
    }
    
    if (prevProps.deleteImageFromGoalError !== this.props.deleteImageFromGoalError && this.props.deleteImageFromGoalError) {
      console.log('Showing delete error alert');
      this._showAlert(
        'error',
        this.props.t ? this.props.t('goal-comment-screen:alert.delete-fail.title') : 'Delete Failed',
        this.props.t ? this.props.t('goal-comment-screen:alert.delete-fail.message') : 'Failed to delete image. Please try again.'
      );
    }

    // Show success alerts for successful operations
    if (prevProps.updateNotesOfGoalSuccess !== this.props.updateNotesOfGoalSuccess && this.props.updateNotesOfGoalSuccess) {
      console.log('Showing save success alert');
      // if ( this.props.setCurrentChild && this.props.currentSettings.child) {
      //   this.props.setCurrentChild(this.props.currentSettings.child);
      // }
      /* this._showAlert(
        'success',
        this.props.t ? this.props.t('goal-comment-screen:alert.save-success.title') : 'Success',
        this.props.t ? this.props.t('goal-comment-screen:alert.save-success.message') : 'Notes saved successfully!'
      ); */
    }

    if (prevProps.uploadImageToGoalSuccess !== this.props.uploadImageToGoalSuccess && this.props.uploadImageToGoalSuccess) {
      console.log('Showing upload success alert');
      this._showAlert(
        'success',
        this.props.t ? this.props.t('goal-comment-screen:alert.upload-success.title') : 'Success',
        this.props.t ? this.props.t('goal-comment-screen:alert.upload-success.message') : 'Image uploaded successfully!'
      );
    }

    if (prevProps.deleteImageFromGoalSuccess !== this.props.deleteImageFromGoalSuccess && this.props.deleteImageFromGoalSuccess) {
      console.log('Showing delete success alert');
      /* this._showAlert(
        'success',
        this.props.t ? this.props.t('goal-comment-screen:alert.delete-success.title') : 'Success',
        this.props.t ? this.props.t('goal-comment-screen:alert.delete-success.message') : 'Image deleted successfully!'
      ); */
    }
  }

  // Add click outside handler to stop deleting mode (matches TouchableWithoutFeedback behavior)
  handleClickOutside = (event: React.MouseEvent) => {
    if (this.state.isDeleting) {
      // Check if click is outside the delete area
      const target = event.target as HTMLElement;
      if (!target.closest('.albums-container') && !target.closest('.delete-button') && !target.closest('.alert-overlay') && !target.closest('.alert-content')) {
        this.setState({ isDeleting: false });
      }
    }
  }

  render() {
    const { 
      loading, 
      hasCameraPermission, 
      hasCameraRollPermission, 
      isDeleting, 
      notes, 
      albums,
      showUploadModal,
      uploadProgress,
      selectedFile,
      showActionSheet,
      actionSheetType,
      showAlert,
      alertType,
      alertTitle,
      alertMessage,
      showUnsavedWarning,
      deleteConfirm,
    } = this.state;
    
    const { 
      updateNotesOfGoalFetching, 
      uploadImageToGoalFetching, 
      deleteImageFromGoalFetching,
      t
    } = this.props;

    

    // Albums Display
    let albumsDisplay = null;
    if (albums && albums.length > 0) {
      if (isDeleting) {
        albumsDisplay = (
          <div className="albums-container">
            <GalleryDeleteComponent 
              albums={albums}
              goalId={this.props.goalId?.toString()}
              onDelete={this._onDeletePhoto}
            />
          </div>
        );
      } else {
        albumsDisplay = (
          <div className="albums-container">
            <GalleryComponent albums={albums} goalId={this.props.goalId?.toString()} previousPage="GoalComment" />
          </div>
        );
      }
    }
    // Remove empty state message - not in reference project

    if (loading) {
      return (
        <div className="goalcomment-page">
          <div className="goalcomment-container">
            <div className="loading-container">
              <div className="loading-spinner">
                {t ? t('goal-comment-screen:loading') : 'Loading goal comment...'}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="goalcomment-page" onClick={this.handleClickOutside}>
        <div className="goalcomment-container">
          {/* Header Section */}
          <header className="page-header">
            <div className="page-header-inner">
              <div className="header-left">
                <button 
                  className="header-button back-button" 
                  onClick={this._onBackPress}
                  aria-label="Go back to goals"
                  title="Go back to goals"
                >
                  {/* <ArrowLeftIcon /> */}
                  <svg width="16" height="11" viewBox="0 0 16 11" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.79646 0.204319C6.07989 0.482194 6.07989 0.943465 5.80201 1.2269L2.42862 4.61141H15.2831C15.6777 4.61141 16 4.93374 16 5.33388C16 5.73402 15.6777 6.05635 15.2831 6.05635H2.42862L5.80757 9.44086C6.08545 9.72429 6.07989 10.18 5.80201 10.4634C5.51858 10.7413 5.06843 10.7413 4.78499 10.4579L0.205627 5.84517C0.144495 5.77848 0.0944773 5.70623 0.0555749 5.61731C0.0166725 5.52839 0 5.43391 0 5.33944C0 5.15048 0.0722476 4.97264 0.205627 4.8337L4.78499 0.220992C5.05731 -0.0679974 5.51303 -0.073555 5.79646 0.204319Z" fill="black"></path></svg>
                </button>
                <h1 className="header-title">
                    {t ? t('goal-comment-screen:title') : 'Goal Comment'}
                  </h1>
              </div>
              <div className="header-center">
                
              </div>
              <div className="header-right">
                {/* Empty for now */}
              </div>
            </div>
          </header>

          {/* Main Content */}
          <div className="page-content">
            {/* Gallery Section */}
            <div className="gallery-section">
              <h2 className="section-header">
                {t ? t('goal-comment-screen:section-title.gallery') : 'GALLERY'}
              </h2>
              
              {/* Albums Display */}
              {albumsDisplay}

              {/* Upload Progress Bar */}
              {uploadImageToGoalFetching && uploadProgress > 0 && (
                <div className="upload-progress-container">
                  <div className="upload-progress-bar">
                    <div 
                      className="upload-progress-fill" 
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <span className="upload-progress-text">{uploadProgress}%</span>
                </div>
              )}

              {/* Upload Button */}
              <div className={`photo-button-outer ${albums.length >= this.photoLimit ? 'disabled' : ''}`}>
                <button 
                  className={`photo-button upload-button`} 
                  onClick={this._onUploadPhoto}
                  disabled={uploadImageToGoalFetching || albums.length >= this.photoLimit}
                  aria-label="Upload photo"
                  title="Upload a new photo"
                >
                  <span className="button-text">
                    {uploadImageToGoalFetching 
                      ? (t ? t('goal-comment-screen:button.uploading') : 'Uploading...') 
                      : (t ? t('goal-comment-screen:button.upload-photo') : 'Upload new photo (max. 4 photos)')
                    }
                  </span>
                </button>
              </div>

              {/* Delete Button - Always visible (matches reference behavior) */}
              <div className="photo-button-outer delete-button-div">
                <button 
                  className={`photo-button delete-button ${isDeleting ? 'active' : ''}`}
                  onClick={this._onDeleteButtonPhoto}
                  disabled={deleteImageFromGoalFetching}
                  aria-label={isDeleting ? 'Exit delete mode' : 'Enter delete mode'}
                  title={isDeleting ? 'Exit delete mode' : 'Enter delete mode to delete photos'}
                >
                  <span className="button-text">
                    {isDeleting 
                      ? (t ? t('goal-comment-screen:button.exit-delete-mode') : 'Exit Delete Mode') 
                      : (t ? t('goal-comment-screen:button.delete-photo') : 'Delete photo')
                    }
                  </span>
                </button>
              </div>
            </div>

            {/* Notes Section */}
            <div className="notes-section">
              <h2 className="section-header">
                {t ? t('goal-comment-screen:section-title.notes') : 'NOTES'}
              </h2>
              <div className="notes-container">
                <textarea
                  className="notes-textarea"
                  rows={5}
                  value={notes}
                  onChange={this._onNotesChange}
                  aria-label="Goal notes"
                  title="Enter your notes about this goal"
                  placeholder={t ? t('goal-comment-screen:placeholder.notes') : 'Enter your notes here...'}
                />
              </div>
            </div>

            {/* Save Button */}
            <div className="save-button-div">
              <button 
                className="save-button comman-btn w-100" 
                onClick={this._onSavePress}
                disabled={updateNotesOfGoalFetching}
                aria-label="Save notes"
                title="Save your notes"
              >
                <span className="button-text">
                  {updateNotesOfGoalFetching 
                    ? (t ? t('goal-comment-screen:button.saving') : 'Saving...') 
                    : (t ? t('goal-comment-screen:button.save') : 'Save')
                  }
                </span>
              </button>
            </div>

            {/* Hidden File Input */}
            <input
              ref={this.fileInputRef}
              type="file"
              accept="image/*"
              onChange={this._onFileSelect}
              style={{ display: 'none' }}
              aria-hidden="true"
            />
          </div>
        </div>

        {/* Upload Action Sheet */}
        {showActionSheet && actionSheetType === 'upload' && (
          <div className="modal-overlay" onClick={() => this.setState({ showActionSheet: false })}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              {/* <div className="cemera-icon"><CameraIcon /></div> */}
              <h3 className="modal-title">
                {t ? t('actionsheet:title.upload-photos') : 'Upload Photos'}
                {/* Allow <strong>CDC Connects</strong> to take pictures and record video? */}
              </h3>
              <div className="modal-options">
                <button 
                  className="modal-option"
                  onClick={() => this._handleActionSheetSelection('camera')}
                  aria-label="Take photo with camera"
                >
                  {t ? t('actionsheet:camera') : 'Camera'}
                </button>
                <button 
                  className="modal-option"
                  onClick={() => this._handleActionSheetSelection('gallery')}
                  aria-label="Select photo from gallery"
                >
                  {t ? t('actionsheet:gallery') : 'Gallery'}
                </button>
                <button 
                  className="modal-option cancel"
                  onClick={() => this._handleActionSheetSelection('cancel')}
                  aria-label="Cancel upload"
                >
                  {t ? t('actionsheet:cancel') : 'Cancel'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Alert Modal */}
        {showAlert && (
          <div className="alert-overlay" onClick={this._hideAlert}>
            <div className="alert-content" onClick={(e) => e.stopPropagation()}>
              <h3 className={`alert-title alert-${alertType}`}>
                {alertTitle}
              </h3>
              <p className="alert-message">
                {alertMessage}
              </p>
              <div className="alert-buttons">
                <button 
                  className="alert-button primary"
                  onClick={this._hideAlert}
                  aria-label="Close alert"
                >
                  {t ? t('alert:ok') : 'OK'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Unsaved Changes Warning */}
        {showUnsavedWarning && (
          <div className="alert-overlay">
            <div className="alert-content">
              <h3 className="alert-title alert-warning">
                {t ? t('goal-comment-screen:alert.back.title') : 'Unsaved Changes'}
              </h3>
              <p className="alert-message">
                {t ? t('goal-comment-screen:alert.back.message') : 'You have unsaved changes. Are you sure you want to leave?'}
              </p>
              <div className="alert-buttons">
                <button 
                  className="alert-button primary alert-button-confirm"
                  onClick={() => this._handleUnsavedChanges(true)}
                  aria-label="Confirm navigation"
                >
                  {t ? t('goal-comment-screen:alert.back.confirm') : 'Leave'}
                </button>
                <button 
                  className="alert-button secondary alert-button-cancel"
                  onClick={() => this._handleUnsavedChanges(false)}
                  aria-label="Cancel navigation"
                >
                  {t ? t('goal-comment-screen:alert.back.cancel') : 'Cancel'}
                </button>
              </div>
            </div>
          </div>
        )}

{deleteConfirm && (
          <div className="alert-overlay">
            <div className="alert-content">
              <h3 className="alert-title alert-warning">
                {t ? t('goal-comment-screen:alert.delete-confirm.title') : 'Message'}
              </h3>
              <p className="alert-message">
                {t ? t('goal-comment-screen:alert.delete-confirm.message') : 'Are you sure you want to delete this file?'}
              </p>
              <div className="alert-buttons">
                <button 
                  className="alert-button primary alert-button-confirm"
                  onClick={() => this._onDeleteConfirm()}
                  aria-label="Confirm navigation"
                >
                  {t ? t('goal-comment-screen:alert.delete-confirm.confirm') : 'Yes'}
                </button>
                <button 
                  className="alert-button secondary alert-button-cancel"
                  onClick={() => this._onDeleteCancel()}
                  aria-label="Cancel navigation"
                >
                  {t ? t('goal-comment-screen:alert.delete-confirm.cancel') : 'Cancel'}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    );
  }
}

const mapStateToProps = (state: any) => {

  // const childId = state.currentSettings?.child?.id;
  // const updatedChild = state.getChildren?.children.find((child: any) => child.id === childId);
  // console.log('updatedChild', 'childId', childId, updatedChild);
  // state.currentSettings.child = updatedChild;

  return {
    currentSettings: state.currentSettings,
    updateNotesOfGoalFetching: state.updateNotesOfGoal?.fetching,
    updateNotesOfGoalSuccess: state.updateNotesOfGoal?.success,
    updateNotesOfGoalError: state.updateNotesOfGoal?.error,
    uploadImageToGoalFetching: state.uploadImageToGoal?.fetching,
    uploadImageToGoalSuccess: state.uploadImageToGoal?.success,
    uploadImageToGoalError: state.uploadImageToGoal?.error,
    deleteImageFromGoalFetching: state.deleteImageFromGoal?.fetching,
    deleteImageFromGoalSuccess: state.deleteImageFromGoal?.success,
    deleteImageFromGoalError: state.deleteImageFromGoal?.error,
  };
};

const mapDispatchToProps = (dispatch: any) => {
  return {
    updateNotesOfGoal: (id: number, notes: string) => dispatch(UpdateNotesOfGoalActions.updateNotesOfGoalRequest(id, notes)),
    uploadImageToGoal: (id: number, image: string) => dispatch(UploadImageToGoalActions.uploadImageToGoalRequest(id, image)),
    deleteImageFromGoal: (id: number, imageId: string) => dispatch(DeleteImageFromGoalActions.deleteImageFromGoalRequest(id, imageId)),
    getChildren: () => dispatch(GetChildrenActions.getChildrenRequest()),
    setCurrentChild: (child: any) => dispatch(CurrentSettingsActions.setCurrentChild(child)),
  };
};

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(GoalCommentPage));
